import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordDetailsCustomer, BookingStatus, PaymentOption } from '@navio/nsdk';
import { NoddiLink, StepperStep } from 'noddi-ui-web';
import {
  customerRoutes,
  getIsPaidInAdvance,
  getJobCompletedAndPaymentLeft,
  getShouldGiveFeedback,
  isMarkedPaidOutsideApp
} from 'noddi-util';
import { RefObject } from 'react';
import { InitiatePaymentSessionButton } from './InitiatePaymentSessionButton';

// we mark all as completed but we show different messages depending on the status
export function getCompletedStep({
  booking,
  key,
  paymentButtonRef
}: {
  booking: BookingRecordDetailsCustomer;
  key: string;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}): StepperStep {
  const {
    isCancelled,
    completedAt,
    order: { isFullyPaid, isManuallyInvoiced },
    isFullyUnableToComplete,
    isPartiallyUnableToComplete
  } = booking;

  const isJobCompletedAndPaymentLeft = getJobCompletedAndPaymentLeft(booking);
  const isFeedbackLeft = getShouldGiveFeedback(booking);

  // `isCancelled` covers a booking canceled by status alone, without `cancelledAt`.
  if (isCancelled) {
    return {
      label: t`Booking cancelled`,
      description: t`Your booking has been cancelled.`,
      key
    };
  }
  if (isFullyUnableToComplete) {
    return {
      label: t`Booking unable to complete`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      description: t`⚠️ Your booking has been unable to complete.`,
      key,
      content: (
        <div className='flex flex-col gap-2'>
          {booking.commentsUnableToCompletePublic && (
            <p className='text-sm text-muted-foreground'>{booking.commentsUnableToCompletePublic}</p>
          )}
          <NoddiLink to={customerRoutes.unableToCompleteDetails.getPath({ slug: booking.slug })}>
            <Trans>See more details</Trans>
          </NoddiLink>
        </div>
      )
    };
  }
  if (isPartiallyUnableToComplete) {
    return {
      label: t`Booking partially completed`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      description: t`⚠️ Some services in your booking were unable to be completed.`,
      key,
      content: (
        <div className='flex flex-col gap-2'>
          {booking.commentsUnableToCompletePublic && (
            <p className='text-sm text-muted-foreground'>{booking.commentsUnableToCompletePublic}</p>
          )}
          <NoddiLink
            endIcon='ArrowRight'
            size='small'
            to={customerRoutes.unableToCompleteDetails.getPath({ slug: booking.slug })}
          >
            <Trans>See more details</Trans>
          </NoddiLink>
        </div>
      )
    };
  }

  if (isJobCompletedAndPaymentLeft) {
    return {
      label: t`Service completed`,
      description: t`Your service has been completed. Please complete your payment.`,
      key,
      content: <InitiatePaymentSessionButton booking={booking} ref={paymentButtonRef} />
    };
  }

  if (isFeedbackLeft) {
    return {
      label: t`Service completed`,
      description: t`Your service has been completed. We would love to hear your thoughts.`,
      key,
      content: (
        <NoddiLink to={customerRoutes.feedbackForBooking.getPath({ slug: booking.slug })}>
          <Trans>Give feedback</Trans>
        </NoddiLink>
      )
    };
  }

  // The step shows before the visit too: it must not read as done, nor as charged before it happens.
  // Status too: a completed booking can lack `completedAt`.
  if (!completedAt && booking.status.value !== BookingStatus.COMPLETED) {
    return { label: t`Service completed`, description: getPaymentAheadDescription(booking), key };
  }

  // A refund still due leaves a prepaid order not "fully paid", yet nothing is owed.
  if (isFullyPaid || getIsPaidInAdvance(booking.order)) {
    return {
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show
      label: `${t`Service completed`} 🎉`,
      description: t`Your service has been completed and payment has been processed.`,
      key
    };
  }

  if (isManuallyInvoiced) {
    return {
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show
      label: `${t`Service completed`} 🎉`,
      description: t`Your service has been completed. You will receive an invoice shortly.`,
      key
    };
  }
  return {
    label: t`Service completed`,
    description: t`Your service has been completed. Please complete your payment.`,
    key
  };
}

function getPaymentAheadDescription(booking: BookingRecordDetailsCustomer): string {
  const { order } = booking;
  // Shop collection is not a Navio charge: the customer settled it already.
  const isCovered =
    getIsPaidInAdvance(order) || isMarkedPaidOutsideApp(order) || (order.isFullyPaid && order.amountPaid.amount > 0);
  if (isCovered) {
    return t`You paid in advance, so there is nothing to pay when the service is done.`;
  }
  // A free order is fully paid without a payment: nothing was paid in advance.
  if (order.isFullyPaid) {
    return t`There is nothing to pay for this booking.`;
  }
  // `isManuallyInvoiced` means an invoice went out, which never happens before the visit.
  if (order.paymentOption === PaymentOption.INVOICE || order.isManuallyInvoiced) {
    return t`You will receive an invoice once the service is done.`;
  }
  return t`You pay once the service is done.`;
}
